import React, { useState } from "react";
import {
    Modal,
    ModalContent,
    ModalDescription,
    ModalHeader,
    ModalTitle,
} from "@/components/ui/Modal";
import SampleBatchSummary from "../SampleBatchSummary";
import SampleStandardOperatingProcedure from "../SampleStandardOperatingProcedure";

export function ViewSampleDetails({ open, onOpenChange, sample }) {
    const [activeTab, setActiveTab] = useState("overview");
    const batchSummary = sample?.batchSummary || {};
    const batchSize = Number(sample?.batchSize) || 0;
    const weightPerPiece = sample?.perPackQuantity ?? batchSummary.perPiece;
    const packetQuantity = sample?.packetQuantity ?? batchSummary.packetQuantity ?? (
        Number(weightPerPiece) > 0 ? Math.floor(batchSize / Number(weightPerPiece)) : null
    );
    const labOutput = sample?.output ?? (
        Number(weightPerPiece) > 0 && Number(packetQuantity) > 0
            ? Number(weightPerPiece) * Number(packetQuantity)
            : null
    );
    const wastage = sample?.wastage ?? (
        labOutput == null ? null : Math.max(0, batchSize - Number(labOutput))
    );
    const recipeStatus = String(sample?.recipe?.recipeStatus || "").toLowerCase();
    const isApproved = sample?.HODStatus === true || recipeStatus.includes("approved") || recipeStatus.includes("final");
    const overview = [
        ["Application Recipe", sample?.recipe?.name || sample?.recipe?.recipeCode],
        ["Batch Size (g)", sample?.batchSize],
        ["Packaging Status", sample?.packagingStatus],
        ["Packaging Type", sample?.packageType?.title],
        ["Weight per Piece (g)", weightPerPiece],
        ["Packet Quantity (pcs)", packetQuantity],
        ["Lab Output (g)", labOutput],
        ["Wastage (g)", wastage],
        ["HOD Status", sample?.HODStatus === true ? "Approved" : sample?.HODStatus === false ? "Not Approved" : "—"],
        ["Application Suggestion", sample?.applicationSuggestion],
    ];
    const tabs = [
        ["overview", "Overview"],
        ["batch-summary", "Batch Summary"],
        ["sop", "SOP"],
    ];

    return (
        <Modal open={open} onOpenChange={onOpenChange}>
            <ModalContent showCloseButton className="max-h-[90vh] max-w-[1200px] gap-0 overflow-y-auto p-5 sm:p-6">
                <ModalHeader className="mb-5 pr-8">
                    <ModalTitle>{sample?.recipe?.name || "View Sample Details"}</ModalTitle>
                    <ModalDescription className="sr-only">
                        Read-only details for the selected sample.
                    </ModalDescription>
                </ModalHeader>

                <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Sample details">
                    {tabs.map(([tab, label]) => (
                        <button
                            key={tab}
                            id={`sample-details-tab-${tab}`}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab}
                            aria-controls={`sample-details-panel-${tab}`}
                            onClick={() => setActiveTab(tab)}
                            className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                                activeTab === tab
                                    ? "border-primary text-foreground"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <section
                    id="sample-details-panel-overview"
                    role="tabpanel"
                    aria-labelledby="sample-details-tab-overview"
                    hidden={activeTab !== "overview"}
                    className="pt-4"
                >
                    <dl className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                        {overview.map(([label, value]) => (
                            <div key={label} className="min-w-0 border-b border-border py-3">
                                <dt className="mb-1 text-xs font-medium text-muted-foreground">
                                    {label}
                                </dt>
                                <dd className="break-words text-sm font-medium text-foreground">
                                    {value ?? "—"}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </section>

                <section
                    id="sample-details-panel-batch-summary"
                    role="tabpanel"
                    aria-labelledby="sample-details-tab-batch-summary"
                    hidden={activeTab !== "batch-summary"}
                    className="pt-5"
                >
                        <SampleBatchSummary
                            value={batchSummary}
                            batchSize={sample?.batchSize}
                            output={sample?.output}
                            recipeFormat={sample?.recipe?.recipeType || "bakery"}
                            showClientCost={!isApproved}
                            showServingDetails={false}
                            disabled
                        />
                </section>

                <section
                    id="sample-details-panel-sop"
                    role="tabpanel"
                    aria-labelledby="sample-details-tab-sop"
                    hidden={activeTab !== "sop"}
                    className="pt-5"
                >
                        <SampleStandardOperatingProcedure
                            value={sample?.sop}
                            recipeFormat={sample?.recipe?.recipeType || "bakery"}
                            showComments={false}
                            disabled
                        />
                </section>
            </ModalContent>
        </Modal>
    );
}